import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const replyToMeetingArticle: BlogArticle = {
  key: BlogArticleKey.REPLY_TO_MEETING,
  slug: BlogSlugs.REPLY_TO_MEETING,
  categoryId: "04",
  date: "2026-08-30",
  readTime: 6,
  locales: {
    fr: {
      title: "Ils répondent, et puis plus rien : rattraper la réponse",
      excerpt:
        "Obtenir une réponse est la partie difficile. La perdre ensuite est la plus fréquente, et ça se joue sur le message suivant, presque toujours écrit trop vite.",
      description:
        "Pourquoi une conversation qui a commencé s'éteint après deux messages, et comment répondre pour qu'elle avance sans forcer le rendez-vous.",
      ctaTitle: "Réponds avec ce que tes appels t'ont appris",
      ctaText:
        "On te propose la suite à partir des objections réellement entendues chez tes clients, par objectif et par canal. Tu relis et tu envoies. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Quelqu'un a répondu. C'est la partie difficile, celle sur laquelle tout le monde écrit, et tu viens de la franchir. Deux messages plus tard, la conversation est morte.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "C'est le moment le moins documenté de la prospection, et de loin le plus coûteux : tu as dépensé l'attention, tu l'as obtenue, et tu la perds sur un message écrit en trente secondes.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Une première réponse n'est pas un accord. C'est une permission d'aller un cran plus loin, et elle se retire aussi vite qu'elle a été donnée.",
        },
        { type: ArticleBlockType.H2, text: "Ce qui tue la conversation" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Presque toujours la même chose : demander le rendez-vous tout de suite. La personne a écrit trois mots pour dire qu'elle voyait de quoi tu parlais, et tu réponds par un lien de réservation.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "De son point de vue, l'écart est brutal. Elle a accordé trente secondes, tu en demandes trente. Rien dans l'échange ne justifie encore ce saut, et le refus poli qui suit n'est pas un refus de ton offre, c'est un refus de l'écart.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce qu'elle a écrit", "Ce que ça veut dire"],
          rows: [
            [
              "« Oui, ça nous parle »",
              "Le problème est reconnu, rien de plus. Il reste à savoir s'il est prioritaire",
            ],
            [
              "« Comment ça marche ? »",
              "Une question de crédibilité, à laquelle un rendez-vous ne répond pas",
            ],
            [
              "« Pas en ce moment »",
              "Le sujet existe, le moment est mauvais. Le pire moment pour insister",
            ],
            [
              "« Envoie-moi de la doc »",
              "Souvent une sortie polie. Parfois une vraie demande, la suite le dira",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ces quatre réponses ressemblent toutes à un feu vert et appellent quatre suites différentes. Les traiter pareil, avec le même lien de réservation, est la façon la plus courante de perdre les quatre.",
        },
        { type: ArticleBlockType.H2, text: "Le message qui fait avancer" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un bon message de suite fait deux choses et une seule à la fois : il renvoie une question à laquelle on répond en une ligne, et il donne quelque chose sans rien demander.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Une question fermée sur leur situation**, pas sur ton offre. *Vous le gérez en interne aujourd'hui, ou c'est confié ?*",
            "**Un élément concret** qui répond à ce qui vient d'être demandé, en deux lignes, pas un document de douze pages.",
            "**Aucune demande de créneau** dans ce message. Le rendez-vous arrive quand la personne a une raison de le vouloir.",
            "**La même longueur que leur réponse.** Répondre par dix lignes à trois mots déséquilibre l'échange.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le dernier point paraît anecdotique et ne l'est pas. La longueur d'un message dit à qui il profite. Un long message après une réponse courte annonce que tu es plus pressé que la personne en face.",
        },
        { type: ArticleBlockType.H2, text: "Deux suites, même première réponse" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "La suite qui referme",
            items: [
              "Un lien de réservation dès la première réponse",
              "Un document envoyé sans avoir été demandé",
              "Une reformulation de ton offre",
              "Une question à laquelle on ne peut pas répondre vite",
              "Une relance le lendemain",
            ],
          },
          good: {
            title: "La suite qui avance",
            items: [
              "Une question fermée sur leur façon de faire",
              "Deux lignes qui répondent exactement à ce qui a été demandé",
              "Un exemple proche de leur situation",
              "Une proposition de créneau seulement quand elle a une raison",
              "Le temps de répondre",
            ],
          },
        },
        { type: ArticleBlockType.H2, text: "Quand proposer le rendez-vous" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le bon moment se reconnaît à un signe simple : la personne pose une question à laquelle un message ne suffit plus à répondre. *Ça donnerait quoi chez nous ?* *Vous feriez comment avec notre outil ?* Là, proposer un créneau ne coupe pas la conversation, il la continue.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Et propose-le en donnant la raison. *Vingt minutes pour regarder votre cas précisément* vaut mieux qu'un lien seul, parce que le lien seul demande à la personne de deviner à quoi elle s'engage.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si le moment ne vient jamais, la conversation n'est pas perdue pour autant. Quelqu'un qui a répondu une fois est infiniment plus atteignable dans trois mois que quelqu'un qui n'a jamais répondu. Note ce qu'il a dit, et reviens quand sa situation aura bougé.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "Les objections que tu reçois en message sont les mêmes que celles de tes appels, formulées plus court. On les remonte depuis tes conversations clients, avec ce qui a fonctionné en face, et on te propose la suite par objectif et par canal. Tu relis, tu corriges, et c'est toi qui envoies.",
          linkLabel: "Découvrir les messages et séquences",
          linkPath: ProtectedPaths.MESSAGES_CONTENT,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ne réponds pas à une première réponse par un lien de réservation. Renvoie une question courte sur leur situation, donne un élément concret sans rien demander, et garde la même longueur qu'eux. Le rendez-vous se propose quand une question dépasse ce qu'un message peut porter, et il se propose avec sa raison.",
        },
      ],
      faq: [
        {
          question: "Que répondre quand un prospect dit « ça nous parle » ?",
          answer:
            "Pas un lien de réservation. Une question fermée sur leur façon de faire aujourd'hui, à laquelle on répond en une ligne. Cette réponse te dit si le problème est seulement reconnu ou s'il est prioritaire, et c'est cette différence qui décide de la suite.",
        },
        {
          question: "Quand proposer un rendez-vous après une réponse ?",
          answer:
            "Quand la personne pose une question qu'un message ne suffit plus à traiter, du type « ça donnerait quoi chez nous ». À ce moment le créneau prolonge la conversation au lieu de l'interrompre. Et propose-le avec sa raison, pas par un lien seul.",
        },
        {
          question: "Que faire quand on te répond « pas en ce moment » ?",
          answer:
            "Rien tout de suite, c'est le pire moment pour insister. Le sujet existe, le calendrier ne suit pas. Note ce qui a été dit et reviens quand la situation aura bougé : quelqu'un qui a répondu une fois reste bien plus atteignable qu'un contact froid.",
        },
        {
          question: "Faut-il envoyer une plaquette quand on te la demande ?",
          answer:
            "Envoie deux lignes qui répondent exactement à la question posée, pas un document de douze pages. La demande de documentation est souvent une sortie polie ; une réponse courte et précise permet de vérifier laquelle des deux c'était.",
        },
      ],
    },
    en: {
      title: "They reply, then nothing: saving the conversation",
      excerpt:
        "Getting a reply is the hard part. Losing it afterwards is the common one, and it turns on the next message, almost always written too fast.",
      description:
        "Why a conversation that started dies after two messages, and how to answer so it moves forward without forcing the meeting.",
      ctaTitle: "Answer with what your calls taught you",
      ctaText:
        "We suggest what comes next from the objections actually heard from your customers, by objective and by channel. You read it back and send. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Someone replied. That is the hard part, the one everybody writes about, and you just cleared it. Two messages later, the conversation is dead.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "It is the least documented moment in outreach, and by far the most expensive: you spent the attention, you got it, and you lose it on a message written in thirty seconds.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A first reply is not agreement. It is permission to go one step further, and it is withdrawn as easily as it was given.",
        },
        { type: ArticleBlockType.H2, text: "What kills the conversation" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Almost always the same thing: asking for the meeting straight away. The person wrote three words to say they saw what you meant, and you answer with a booking link.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "From their side the jump is brutal. They granted thirty seconds, you are asking for thirty minutes. Nothing in the exchange justifies that yet, and the polite refusal that follows is not a refusal of your offer, it is a refusal of the jump.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What they wrote", "What it means"],
          rows: [
            [
              "Yes, that speaks to us",
              "The problem is recognised, nothing more. Whether it is a priority is still open",
            ],
            ["How does it work?", "A credibility question, which a meeting does not answer"],
            ["Not right now", "The subject exists, the timing does not. The worst moment to push"],
            [
              "Send me some material",
              "Often a polite exit. Sometimes a real request, what follows will tell",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "All four look like a green light and call for four different continuations. Treating them alike, with the same booking link, is the most common way to lose all four.",
        },
        { type: ArticleBlockType.H2, text: "The message that moves things forward" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A good next message does two things, and only one at a time: it sends back a question answerable in one line, and it gives something without asking for anything.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**A closed question about their situation**, not about your offer. *Do you handle it in-house today, or is it outsourced?*",
            "**One concrete element** answering what was just asked, in two lines, not a twelve-page document.",
            "**No slot request** in that message. The meeting comes when the person has a reason to want it.",
            "**The same length as their reply.** Answering three words with ten lines unbalances the exchange.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The last point sounds trivial and is not. The length of a message says who it serves. A long message after a short reply announces that you are in more of a hurry than the person opposite.",
        },
        { type: ArticleBlockType.H2, text: "Two continuations, same first reply" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "The continuation that closes",
            items: [
              "A booking link on the first reply",
              "A document sent without being asked for",
              "A restatement of your offer",
              "A question that cannot be answered quickly",
              "A follow-up the next day",
            ],
          },
          good: {
            title: "The continuation that advances",
            items: [
              "A closed question about how they work",
              "Two lines answering exactly what was asked",
              "An example close to their situation",
              "A slot proposed only once there is a reason",
              "Time to answer",
            ],
          },
        },
        { type: ArticleBlockType.H2, text: "When to propose the meeting" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The right moment has a simple tell: the person asks something a message can no longer answer. *What would that look like here?* *How would you handle it with our stack?* At that point proposing a slot does not cut the conversation, it continues it.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "And propose it with its reason. *Twenty minutes to look at your case specifically* beats a bare link, because a bare link asks the person to guess what they are agreeing to.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If the moment never comes, the conversation is not lost. Someone who replied once is far more reachable in three months than someone who never did. Note what they said, and come back when their situation has moved.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "The objections you get in messages are the same as the ones in your calls, phrased shorter. We surface them from your customer conversations, along with what worked in reply, and suggest what comes next by objective and by channel. You read it back, you correct it, and you are the one who sends.",
          linkLabel: "See how messages and sequences work",
          linkPath: ProtectedPaths.MESSAGES_CONTENT,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Do not answer a first reply with a booking link. Send back a short question about their situation, give one concrete element without asking for anything, and match their length. The meeting is proposed when a question outgrows what a message can carry, and it is proposed with its reason.",
        },
      ],
      faq: [
        {
          question: "What should you reply when a prospect says it speaks to them?",
          answer:
            "Not a booking link. A closed question about how they work today, answerable in one line. That answer tells you whether the problem is merely recognised or actually a priority, and that difference decides what comes next.",
        },
        {
          question: "When should you propose a meeting after a reply?",
          answer:
            "When the person asks something a message can no longer handle, along the lines of what would that look like here. At that point a slot continues the conversation instead of interrupting it. And propose it with its reason, not as a bare link.",
        },
        {
          question: "What do you do when someone says not right now?",
          answer:
            "Nothing immediately, it is the worst moment to push. The subject exists, the calendar does not follow. Note what was said and come back when the situation has moved: someone who replied once stays far more reachable than a cold contact.",
        },
        {
          question: "Should you send material when asked for it?",
          answer:
            "Send two lines answering exactly the question asked, not a twelve-page document. A request for documentation is often a polite exit; a short precise answer lets you find out which of the two it was.",
        },
      ],
    },
  },
};
